import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { Badge, Button, Drawer, Popover, Segmented, Space, Tag, Tooltip } from 'antd'
import {
  BugOutlined,
  CloseOutlined,
  CodeOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  LayoutOutlined,
  LinkOutlined,
  MenuUnfoldOutlined,
  PlayCircleOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { ROUTES } from '@/config/routes.config'
import './devtools.css'
import { devDocSections } from './dev-doc-links'
import ComponentOutlineControls from './ComponentOutlineControls'
import DevToolsThemeSection from './DevToolsThemeSection'
import TestSessionPanel from './test-session/TestSessionPanel'
import MenuPermissionInspector from './MenuPermissionInspector'
import { useComponentOutlineControls } from './useComponentOutlineControls'
import { diagnosticsStore } from './diagnostics/diagnostic-store'
import DiagnosticsPanel from './diagnostics/DiagnosticsPanel'
import { endTestSession, startTestSession } from './test-session/test-session-controls'
import { testSessionStore } from './test-session/test-session-store'
import QuickProblemMarkModal from './test-session/QuickProblemMarkModal'
import { openQuickProblemMarkModal } from './test-session/quick-problem-mark-store'

type BaseDevToolAction = {
  key: string
  label: string
  description: string
  icon?: ReactNode
}

export type DevToolAction =
  | (BaseDevToolAction & {
      to: string
      onClick?: never
    })
  | (BaseDevToolAction & {
      to?: never
      onClick: () => void
    })

type DevToolsPanelProps = {
  actions?: DevToolAction[]
  navigate?: (to: string) => void | Promise<void>
}

type DevPanelTabKey = 'tools' | 'menu-permissions' | 'diagnostics' | 'testing' | 'docs'
type DevToolsDisplayMode = 'drawer' | 'inline'

const PANEL_PREFERENCES_KEY = 'nsy.devtools.panel.preferences'
const DEFAULT_INLINE_WIDTH = 380
const MIN_INLINE_WIDTH = 320
const MAX_INLINE_WIDTH_RATIO = 0.6
const INLINE_MODE_BREAKPOINT = 1024

type DevToolsPanelPreferences = {
  mode: DevToolsDisplayMode
  width: number
}

function readPanelPreferences(): DevToolsPanelPreferences {
  try {
    const stored = window.localStorage.getItem(PANEL_PREFERENCES_KEY)
    if (!stored) return { mode: 'drawer', width: DEFAULT_INLINE_WIDTH }
    const parsed = JSON.parse(stored) as Partial<DevToolsPanelPreferences>
    return {
      mode: parsed.mode === 'inline' ? 'inline' : 'drawer',
      width: typeof parsed.width === 'number' && Number.isFinite(parsed.width)
        ? Math.max(MIN_INLINE_WIDTH, Math.round(parsed.width))
        : DEFAULT_INLINE_WIDTH,
    }
  } catch {
    return { mode: 'drawer', width: DEFAULT_INLINE_WIDTH }
  }
}

function savePanelPreferences(preferences: DevToolsPanelPreferences) {
  try {
    window.localStorage.setItem(PANEL_PREFERENCES_KEY, JSON.stringify(preferences))
  } catch {
    // DevTool preferences are optional when storage is unavailable.
  }
}

function clampInlineWidth(width: number, viewportWidth: number) {
  const maxWidth = Math.max(MIN_INLINE_WIDTH, Math.floor(viewportWidth * MAX_INLINE_WIDTH_RATIO))
  return Math.min(maxWidth, Math.max(MIN_INLINE_WIDTH, Math.round(width)))
}

function isRouteAction(
  action: DevToolAction
): action is BaseDevToolAction & { to: string; onClick?: never } {
  return typeof action.to === 'string'
}

export default function DevToolsPanel({ actions = [], navigate }: DevToolsPanelProps) {
  const initialPreferences = useMemo(() => readPanelPreferences(), [])
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<DevPanelTabKey>('tools')
  const [entryHovered, setEntryHovered] = useState(false)
  const [displayMode, setDisplayMode] = useState<DevToolsDisplayMode>(initialPreferences.mode)
  const [inlineWidth, setInlineWidth] = useState(initialPreferences.width)
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth)
  const [resizing, setResizing] = useState(false)
  const resizeStartRef = useRef({ pointerX: 0, width: DEFAULT_INLINE_WIDTH })
  const originalBodyPaddingRef = useRef<string | null>(null)
  const originalUserSelectRef = useRef<string | null>(null)
  const originalCursorRef = useRef<string | null>(null)
  const diagnosticCount = useSyncExternalStore(
    (listener) => diagnosticsStore.subscribe(listener),
    () => diagnosticsStore.getEvents().length,
    () => diagnosticsStore.getEvents().length
  )
  const testSessionActive = useSyncExternalStore(
    (listener) => testSessionStore.subscribe(listener),
    () => testSessionStore.getSnapshot().active,
    () => testSessionStore.getSnapshot().active,
  )
  const componentOutlineControls = useComponentOutlineControls()
  const inlineModeSupported = viewportWidth >= INLINE_MODE_BREAKPOINT
  const effectiveDisplayMode: DevToolsDisplayMode = displayMode === 'inline' && inlineModeSupported
    ? 'inline'
    : 'drawer'
  const renderedInlineWidth = clampInlineWidth(inlineWidth, viewportWidth)

  useEffect(() => {
    savePanelPreferences({ mode: displayMode, width: inlineWidth })
  }, [displayMode, inlineWidth])

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (open && effectiveDisplayMode === 'inline') {
      if (originalBodyPaddingRef.current === null) {
        originalBodyPaddingRef.current = document.body.style.paddingRight
      }
      document.body.style.paddingRight = `${renderedInlineWidth}px`
      return
    }

    if (originalBodyPaddingRef.current !== null) {
      document.body.style.paddingRight = originalBodyPaddingRef.current
      originalBodyPaddingRef.current = null
    }
  }, [effectiveDisplayMode, open, renderedInlineWidth])

  useEffect(() => () => {
    if (originalBodyPaddingRef.current !== null) {
      document.body.style.paddingRight = originalBodyPaddingRef.current
    }
    if (originalUserSelectRef.current !== null) {
      document.body.style.userSelect = originalUserSelectRef.current
    }
    if (originalCursorRef.current !== null) {
      document.body.style.cursor = originalCursorRef.current
    }
  }, [])

  useEffect(() => {
    if (!resizing) return

    const handlePointerMove = (event: PointerEvent) => {
      const nextWidth = resizeStartRef.current.width + resizeStartRef.current.pointerX - event.clientX
      setInlineWidth(clampInlineWidth(nextWidth, viewportWidth))
    }
    const finishResize = () => setResizing(false)

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', finishResize)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', finishResize)
      if (originalUserSelectRef.current !== null) {
        document.body.style.userSelect = originalUserSelectRef.current
        originalUserSelectRef.current = null
      }
      if (originalCursorRef.current !== null) {
        document.body.style.cursor = originalCursorRef.current
        originalCursorRef.current = null
      }
    }
  }, [resizing, viewportWidth])

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const target = event.target
      if (event.defaultPrevented || event.ctrlKey || event.metaKey) return
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || (target instanceof HTMLElement && target.isContentEditable)) return
      if (!event.altKey || !event.shiftKey || event.key.toLowerCase() !== 't') return
      event.preventDefault()
      if (testSessionStore.getSnapshot().active) endTestSession()
      else startTestSession()
    }
    window.addEventListener('keydown', handleShortcut)
    return () => window.removeEventListener('keydown', handleShortcut)
  }, [])

  const builtInActions = useMemo<DevToolAction[]>(
    () => [
      {
        key: 'demo',
        label: '开发示例',
        description: '调试页面、组件示例与临时验证入口',
        icon: <CodeOutlined />,
        to: ROUTES.demo.root,
      },
    ],
    []
  )

  const toolActions = [...builtInActions, ...actions]
  const primaryToolAction = toolActions[0]
  const secondaryToolActions = toolActions.slice(1)
  const docCount = devDocSections.reduce((count, section) => count + section.items.length, 0)
  const openExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }


  const closePrimaryPanel = () => {
    setOpen(false)
    setEntryHovered(false)
  }

  const beginInlineResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    resizeStartRef.current = { pointerX: event.clientX, width: renderedInlineWidth }
    originalUserSelectRef.current = document.body.style.userSelect
    originalCursorRef.current = document.body.style.cursor
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    setResizing(true)
  }

  const handleResizeKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const delta = event.key === 'ArrowLeft' ? 16 : -16
    setInlineWidth((currentWidth) => clampInlineWidth(currentWidth + delta, viewportWidth))
  }

  const openDiagnostics = () => {
    setActiveTab('diagnostics')
    setOpen(true)
  }

  const openTesting = () => {
    setActiveTab('testing')
    setOpen(true)
  }

  const tabs = [
    { key: 'tools' as const, label: '工具', icon: <ToolOutlined /> },
    { key: 'menu-permissions' as const, label: '菜单&权限', icon: <SafetyCertificateOutlined /> },
    { key: 'diagnostics' as const, label: '诊断', icon: <BugOutlined /> },
    { key: 'testing' as const, label: '测试', icon: <ExperimentOutlined /> },
    { key: 'docs' as const, label: '文档', icon: <FileTextOutlined /> },
  ]

  const runAction = (action: DevToolAction) => {
    if (isRouteAction(action)) {
      if (navigate) {
        void navigate(action.to)
      } else {
        window.history.pushState(null, '', action.to)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    } else {
      action.onClick()
    }
    closePrimaryPanel()
  }

  const renderToolsTab = () => (
    <>
      <section className="rounded bg-white p-3 shadow-sm">
        {primaryToolAction ? (
          <button
            type="button"
            onClick={() => runAction(primaryToolAction)}
            className="flex w-full items-center gap-4 rounded-lg bg-orange-50 px-4 py-4 text-left transition-colors hover:bg-orange-100"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white text-xl text-orange-500 shadow-sm">
              {primaryToolAction.icon ?? <ToolOutlined />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-base font-semibold leading-6 text-gray-800">
                {primaryToolAction.label}
              </span>
              <span className="mt-1 block text-sm leading-6 text-gray-500">
                {primaryToolAction.description}
              </span>
            </span>
            <RightOutlined className="shrink-0 text-sm text-orange-400" />
          </button>
        ) : null}

        {secondaryToolActions.length > 0 ? (
          <div className="mt-3">
            <div className="mb-2 text-xs font-medium text-gray-700">附加动作</div>
            <div className="grid gap-2">
              {secondaryToolActions.map((action) => (
                <Button
                  key={action.key}
                  block
                  onClick={() => runAction(action)}
                  style={{ height: 'auto', padding: '12px', textAlign: 'left' }}
                >
                  <span className="flex w-full items-center gap-3 overflow-hidden">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-orange-50 text-base text-orange-500">
                      {action.icon ?? <ToolOutlined />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium leading-5 text-gray-700">
                        {action.label}
                      </span>
                      <span className="block truncate text-xs leading-5 text-gray-400">
                        {action.description}
                      </span>
                    </span>
                    <RightOutlined className="shrink-0 text-xs text-gray-300" />
                  </span>
                </Button>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      <ComponentOutlineControls {...componentOutlineControls} />
      <DevToolsThemeSection />
    </>
  )

  const renderMenuPermissionsTab = () => (
    <MenuPermissionInspector
      permissionOutlineEnabled={componentOutlineControls.permissionOutlineEnabled}
      setPermissionOutlineEnabled={componentOutlineControls.setPermissionOutlineEnabled}
      onLocateTarget={effectiveDisplayMode === 'drawer' ? closePrimaryPanel : undefined}
    />
  )

  const renderDiagnosticsTab = () => (
    <>
      <DiagnosticsPanel />
    </>
  )

  const renderDocsTab = () => (
    <section className="rounded bg-white p-3 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-gray-800">文档入口</div>
          <div className="mt-0.5 text-xs text-gray-400">按技术域整理，保持轻量但可扩展</div>
        </div>
        <Tag>{docCount}</Tag>
      </div>

      <div className="space-y-2">
        {devDocSections.map((section) => (
          <div key={section.key} className="rounded border border-gray-100 bg-gray-50 px-3 py-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-700">{section.label}</div>
                <div className="mt-0.5 text-xs leading-5 text-gray-400">{section.description}</div>
              </div>
              <Tag className="shrink-0">{section.items.length}</Tag>
            </div>

            <div className="mt-2 space-y-1.5">
              {section.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => openExternalLink(item.url)}
                  className="flex w-full items-start gap-3 rounded bg-white px-3 py-2 text-left transition-colors hover:bg-sky-50"
                >
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded bg-sky-50 text-xs text-sky-600">
                    <LinkOutlined />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="min-w-0 truncate text-sm font-medium leading-5 text-gray-700">
                        {item.label}
                      </span>
                      {item.version ? (
                        <Tag className="m-0 shrink-0 px-1 text-[10px] leading-4">
                          {item.version}
                        </Tag>
                      ) : null}
                    </span>
                    <span className="block text-xs leading-5 text-gray-400">
                      {item.description}
                    </span>
                  </span>
                  <RightOutlined className="mt-1 shrink-0 text-xs text-gray-300" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'tools':
        return renderToolsTab()
      case 'menu-permissions':
        return renderMenuPermissionsTab()
      case 'diagnostics':
        return renderDiagnosticsTab()
      case 'testing':
        return <TestSessionPanel />
      case 'docs':
        return renderDocsTab()
      default:
        return null
    }
  }

  const panelModeControl = (
    <Segmented<DevToolsDisplayMode>
      aria-label="开发工具展示模式"
      size="small"
      value={effectiveDisplayMode}
      options={[
        { label: '抽屉', value: 'drawer', icon: <MenuUnfoldOutlined /> },
        { label: '内嵌', value: 'inline', icon: <LayoutOutlined />, disabled: !inlineModeSupported },
      ]}
      onChange={setDisplayMode}
    />
  )

  const panelTitle = (
    <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
      <Space size={8}>
        <ToolOutlined />
        <span>开发工具</span>
      </Space>
      {panelModeControl}
    </div>
  )

  const panelContent = (
    <div className="space-y-4" data-c="DevToolsPanel">
      <section className="rounded bg-white p-1 shadow-sm">
        <div className="grid grid-cols-5 gap-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key
            return (
              <Button
                key={tab.key}
                type="text"
                aria-pressed={isActive}
                onClick={() => setActiveTab(tab.key)}
                className={isActive ? '!bg-color-primary-bg !text-primary-600' : '!text-gray-500'}
                style={{ height: 'auto', padding: '10px 6px' }}
              >
                <span className="flex flex-col items-center gap-1 text-xs">
                  <span className="text-sm">{tab.icon}</span>
                  <span>{tab.label}</span>
                </span>
              </Button>
            )
          })}
        </div>
      </section>

      {renderActiveTab()}
    </div>
  )

  return (
    <>
      <div
        data-c="DevToolsPanel"
        data-testid="devtools-entry"
        className="rr-block"
        style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999, width: 40, height: 136, pointerEvents: 'none' }}
        onMouseLeave={() => setEntryHovered(false)}
      >
        {diagnosticCount > 0 ? (
          <div
            aria-hidden={!entryHovered}
            className={`absolute bottom-24 right-0 transition-all duration-200 ease-out ${entryHovered ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'}`}
            data-testid="devtools-diagnostics-shortcut"
          >
            <Badge count={diagnosticCount} offset={[-8, 0]} overflowCount={99} size="small">
              <Tooltip title="故障记录" placement="left">
                <Button
                  aria-label="打开故障记录"
                  shape="circle"
                  size="large"
                  tabIndex={entryHovered ? 0 : -1}
                  icon={<BugOutlined />}
                  onClick={openDiagnostics}
                  className="!border-gray-200 !bg-white !text-red-500 !shadow-[0_6px_18px_rgba(0,0,0,0.12)]"
                />
              </Tooltip>
            </Badge>
          </div>
        ) : null}
        <div
          aria-hidden={!entryHovered}
          className={`absolute bottom-12 right-0 transition-all duration-200 ease-out ${entryHovered ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'}`}
          data-testid="devtools-test-shortcut"
        >
          <Popover
            trigger="hover"
            mouseEnterDelay={0}
            placement="left"
            content={<div className="rr-block flex flex-col gap-2" data-c="DevToolsPanel" data-testid="devtools-test-session-menu">
              <Button
                aria-label={testSessionActive ? '结束测试会话' : '开始测试会话'}
                size="small"
                icon={testSessionActive ? <StopOutlined /> : <PlayCircleOutlined />}
                onClick={testSessionActive ? endTestSession : startTestSession}
              >
                {testSessionActive ? '结束测试' : '开始测试'}
              </Button>
              {testSessionActive ? <Button size="small" onClick={openQuickProblemMarkModal}>标记问题</Button> : null}
            </div>}
          >
            <Button
              aria-label="打开测试工具"
              shape="circle"
              size="large"
              tabIndex={entryHovered ? 0 : -1}
              icon={<ExperimentOutlined />}
              onClick={openTesting}
              className={testSessionActive ? '!border-orange-200 !bg-orange-50 !text-orange-600 !shadow-[0_6px_18px_rgba(0,0,0,0.12)]' : '!border-gray-200 !bg-white !text-sky-600 !shadow-[0_6px_18px_rgba(0,0,0,0.12)]'}
            />
          </Popover>
        </div>
        <div className="pointer-events-auto absolute bottom-0 right-0" onMouseEnter={() => setEntryHovered(true)}>
          <span className="inline-block" data-testid="devtools-diagnostics-notice">
            <Badge count={entryHovered ? 0 : diagnosticCount} offset={[-8, 0]} overflowCount={99} size="small">
              <Tooltip title="开发工具" placement="left">
                <Button
                  aria-label="打开开发工具"
                  type="primary"
                  shape="circle"
                  size="large"
                  icon={<ToolOutlined />}
                  onClick={() => setOpen(true)}
                  className="!shadow-[0_6px_18px_color-mix(in_srgb,var(--color-primary)_28%,transparent)]"
                />
              </Tooltip>
            </Badge>
          </span>
        </div>
      </div>

      {effectiveDisplayMode === 'drawer' ? (
        <Drawer
          title={panelTitle}
          open={open}
          onClose={closePrimaryPanel}
          placement="right"
          size={380}
          rootClassName="rr-block"
          destroyOnHidden
          styles={{
            header: { paddingRight: 12 },
            body: { padding: 16, background: '#f7f8fa' },
          }}
        >
          {panelContent}
        </Drawer>
      ) : open ? (
        <aside
          aria-label="开发工具"
          className="rr-block fixed inset-y-0 right-0 z-[10000] flex flex-col border-l border-gray-200 bg-white shadow-[-8px_0_24px_rgba(0,0,0,0.08)]"
          data-c="DevToolsPanel"
          data-testid="devtools-inline-panel"
          style={{ width: renderedInlineWidth }}
        >
          <div
            aria-label="调整开发工具宽度"
            aria-orientation="vertical"
            aria-valuemax={Math.floor(viewportWidth * MAX_INLINE_WIDTH_RATIO)}
            aria-valuemin={MIN_INLINE_WIDTH}
            aria-valuenow={renderedInlineWidth}
            className="group absolute inset-y-0 left-0 z-10 w-2 -translate-x-1/2 cursor-col-resize touch-none outline-none"
            role="separator"
            tabIndex={0}
            onDoubleClick={() => setInlineWidth(DEFAULT_INLINE_WIDTH)}
            onKeyDown={handleResizeKeyDown}
            onPointerDown={beginInlineResize}
          >
            <span className="mx-auto block h-full w-px bg-gray-200 transition-colors group-hover:bg-primary group-focus:bg-primary" />
          </div>

          <header className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-100 px-4">
            {panelTitle}
            <Tooltip title="关闭">
              <Button
                aria-label="关闭开发工具"
                shape="circle"
                type="text"
                icon={<CloseOutlined />}
                onClick={closePrimaryPanel}
              />
            </Tooltip>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto bg-[#f7f8fa] p-4">
            {panelContent}
          </div>
        </aside>
      ) : null}
      <QuickProblemMarkModal />
    </>
  )
}
